import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Card,
  Button,
  Select,
  Table,
  Typography,
  Row,
  Col,
  Spin,
  notification,
  Divider,
  Switch,
  Space,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
// Note: These imports will need to be updated with the actual API functions
// The user mentioned they will implement the fetchAPI themselves
import {
  getCashById,
  getCashPartnerInvoices,
  getFirstPagePartnerInvoicesForCash,
  allocateCash,
  getAllPartners,
  clearAccountingCache,
} from "../../../api/accounting";
import "bootstrap/dist/css/bootstrap.min.css";
import "../Utils/BankStatements.css"; // reusing the same CSS for consistent styling
// Import column definitions
import getCashColumns from "../Columns/CashAllocation/cashColumns";
import getInvoiceColumns from "../Columns/CashAllocation/invoiceColumns";

const { Text, Title } = Typography;
const { Option } = Select;

// Helper to parse numbers that may contain commas, dollar signs, or other formatting
// parseFloat("18,132") returns 18, but we want 18132
// parseFloat("$20,670.60") returns NaN, but we want 20670.60
const parseNumberWithCommas = (value) => {
  if (value == null || value === undefined || value === "") return 0;
  // Convert to string and remove currency symbols ($, €, £, etc.), commas, and spaces
  const cleanedValue = String(value)
    .replace(/[$€£¥₹,\s]/g, "") // Remove currency symbols, commas, and spaces
    .trim();
  const parsed = parseFloat(cleanedValue);
  return isNaN(parsed) ? 0 : parsed;
};

const CashAllocation = ({
  cashId,
  selectedBrand,
  selectedBrands: selectedBrandsProp,
  onSuccess,
  onCancel,
  onViewPartner,
}) => {
  // Support both single brand and multiple brands
  const selectedBrands =
    selectedBrandsProp || (selectedBrand ? [selectedBrand] : []);
  const primaryBrand = selectedBrands.length > 0 ? selectedBrands[0] : null;
  const navigate = useNavigate();
  const [loadingCash, setLoadingCash] = useState(false);
  const [cash, setCash] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [allocationStatus, setAllocationStatus] = useState(null);
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [loadingAllocation, setLoadingAllocation] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [showPaidInvoices, setShowPaidInvoices] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  // Background loading state for hybrid loading
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const backgroundLoadingRef = useRef(false);
  const backgroundAbortRef = useRef(false);

  // Refs to prevent duplicate requests
  const fetchingCashRef = useRef(false);
  const fetchingInvoicesRef = useRef(false);
  const fetchingPartnersRef = useRef(false);
  const lastFetchKeyRef = useRef(null);

  // Fetch cash details
  const fetchCash = useCallback(async () => {
    // Prevent duplicate requests
    if (fetchingCashRef.current) return;
    fetchingCashRef.current = true;

    setLoadingCash(true);
    try {
      const response = await getCashById(cashId);
      const data = response.data;

      setCash(data);

      // Cash data now includes cash_allocations, allocated_amount, and allocation_status
      // directly from the backend
      if (data.cash_allocations) {
        setAllocations(data.cash_allocations);
      }

      // Update allocation status using data from backend
      updateAllocationStatus(data);

      // If cash has a partner, pre-select it and fetch invoices
      // Check multiple possible field names for partner ID
      // Handle case where partner might be an object with id property
      let partnerId =
        data.invoice_partner ||
        data.partner ||
        data.partner_id ||
        data.invoice_partner_id;

      // Extract ID if partnerId is an object
      if (partnerId && typeof partnerId === "object") {
        partnerId = partnerId.id || partnerId.partner_id;
      }

      if (partnerId) {
        // Only set the partner - useEffect on selectedPartner will handle fetching invoices
        setSelectedPartner(partnerId);
      }
    } catch (error) {
      console.error("Error fetching cash:", error);

      // Check if it's a 404 error (cash not found)
      if (error.response && error.response.status === 404) {
        notification.error({
          message: "Cash Not Found",
          description: `Cash with ID ${cashId} does not exist.`,
        });
        // Navigate back to cash after a delay
        setTimeout(() => {
          handleBack();
        }, 2000);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to load cash details",
        });
      }
    } finally {
      setLoadingCash(false);
      fetchingCashRef.current = false;
    }
  }, [cashId]);

  // Fetch cash data
  useEffect(() => {
    if (!cashId) return;
    fetchCash();
  }, [cashId, fetchCash]);

  // Fetch all partners
  useEffect(() => {
    if (!selectedBrands || selectedBrands.length === 0) return;

    const fetchPartners = async () => {
      // Prevent duplicate requests
      if (fetchingPartnersRef.current) return;
      fetchingPartnersRef.current = true;

      try {
        // Fetch partners for all selected brands
        const brandIds = selectedBrands.map((b) => b.id).join(",");
        const data = await getAllPartners({
          brand: brandIds,
          // No type filter to get all partners
        });
        // Deduplicate partners by ID (same partner can exist across multiple brands)
        const rawPartnersList = Array.isArray(data.data) ? data.data : [];
        const seenIds = new Set();
        const seenNames = new Set();
        const partnersList = rawPartnersList.filter((partner) => {
          if (seenIds.has(partner.id)) {
            return false;
          }
          const normalizedName = (partner.name || "").toLowerCase().trim();
          if (seenNames.has(normalizedName)) {
            return false;
          }
          seenIds.add(partner.id);
          seenNames.add(normalizedName);
          return true;
        });
        setPartners(partnersList);
      } catch (error) {
        console.error("Error fetching partners:", error);
        // Initialize as empty array on error
        setPartners([]);
      } finally {
        fetchingPartnersRef.current = false;
      }
    };

    fetchPartners();
  }, [selectedBrands]);

  // Helper to process invoice data
  const processInvoiceData = useCallback(
    (data) => {
      return data.map((invoice) => {
        const currentAllocationAmount = parseFloat(
          invoice.current_allocation_amount || 0
        );
        const totalAmount = parseFloat(invoice.total_amount || 0);
        const paidAmount = parseFloat(invoice.paid_amount || 0);
        const remainingAmount = totalAmount - paidAmount;

        const existingAllocation = allocations.find(
          (alloc) =>
            alloc.invoice === invoice.id || alloc.invoice_id === invoice.id
        );

        const allocationAmount = Math.max(
          currentAllocationAmount,
          existingAllocation ? parseFloat(existingAllocation.amount || 0) : 0
        );

        return {
          ...invoice,
          cashAmount: allocationAmount > 0 ? allocationAmount : 0,
          cashNotes: "",
          remainingAmount: remainingAmount,
          totalAmount,
          paidAmount,
          is_allocated: allocationAmount > 0,
        };
      });
    },
    [allocations]
  );

  // Fetch invoices function - HYBRID LOADING: first page fast, rest in background
  const fetchInvoices = async (partnerId, showPaidInvoices = false) => {
    if (!partnerId || !selectedBrands || selectedBrands.length === 0) return;

    // Create a unique key for this request to prevent duplicates
    const fetchKey = `${cashId}-${partnerId}-${showPaidInvoices}`;
    if (fetchingInvoicesRef.current && lastFetchKeyRef.current === fetchKey) {
      return; // Skip duplicate request
    }

    fetchingInvoicesRef.current = true;
    lastFetchKeyRef.current = fetchKey;

    // Abort any ongoing background loading
    backgroundAbortRef.current = true;
    setLoadingInvoices(true);

    try {
      const brandIds = selectedBrands.map((b) => b.id).join(",");
      const baseParams = {
        brand: brandIds,
        partner: partnerId,
        cash_id: cashId,
        show_paid_invoices: showPaidInvoices,
      };

      // ========== PHASE 1: Quick first page load ==========
      const firstPageResult = await getFirstPagePartnerInvoicesForCash(
        baseParams
      );
      const firstPageData = firstPageResult.results || [];

      // Process and display first page immediately
      const processedFirstPage = processInvoiceData(firstPageData);
      setInvoices(processedFirstPage);

      // Update selected invoice IDs for allocated invoices
      const allocatedIds = processedFirstPage
        .filter((inv) => inv.is_allocated)
        .map((inv) => inv.id);
      if (allocatedIds.length > 0) {
        setSelectedInvoiceIds((prev) => [
          ...new Set([...prev, ...allocatedIds]),
        ]);
      }

      setLoadingInvoices(false);

      // ========== PHASE 2: Load remaining data in background ==========
      if (firstPageResult.hasMore) {
        backgroundAbortRef.current = false;
        backgroundLoadingRef.current = true;
        setIsLoadingMore(true);

        setTimeout(async () => {
          try {
            const fullResponse = await getCashPartnerInvoices(baseParams);

            if (backgroundAbortRef.current) {
              backgroundLoadingRef.current = false;
              setIsLoadingMore(false);
              return;
            }

            let fullData;
            if (Array.isArray(fullResponse)) {
              fullData = fullResponse;
            } else if (fullResponse && Array.isArray(fullResponse.data)) {
              fullData = fullResponse.data;
            } else if (fullResponse && Array.isArray(fullResponse.results)) {
              fullData = fullResponse.results;
            } else {
              fullData = [];
            }

            const processedFull = processInvoiceData(fullData);
            setInvoices(processedFull);

            const allAllocatedIds = processedFull
              .filter((inv) => inv.is_allocated)
              .map((inv) => inv.id);
            if (allAllocatedIds.length > 0) {
              setSelectedInvoiceIds((prev) => [
                ...new Set([...prev, ...allAllocatedIds]),
              ]);
            }
          } catch (bgError) {
            console.error("Background loading error:", bgError);
          } finally {
            backgroundLoadingRef.current = false;
            setIsLoadingMore(false);
          }
        }, 100);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      setInvoices([]);
      setLoadingInvoices(false);
    } finally {
      // Allow new requests after a short delay to prevent rapid re-fetches
      setTimeout(() => {
        fetchingInvoicesRef.current = false;
      }, 500);
    }
  };

  // Fetch invoices when partner changes
  useEffect(() => {
    if (selectedPartner) {
      fetchInvoices(selectedPartner, showPaidInvoices);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartner, selectedBrands]);

  // Handle partner change - useEffect on selectedPartner will handle fetching invoices
  const handlePartnerChange = (value) => {
    setSelectedPartner(value);
    // Clear invoices when partner changes
    setInvoices([]);
    // Invoice fetching is handled by useEffect on selectedPartner
  };

  // Handle cash allocation
  const handleAllocateCash = async (invoiceId, amount, allocationId = null) => {
    // Find the invoice to get its number for messages
    const invoice = invoices.find((inv) => inv.id === invoiceId);
    const invoiceNumber = invoice ? invoice.number : invoiceId;

    // Convert amount to number if it's a string (handle commas)
    const cashAmount =
      typeof amount === "string" ? parseNumberWithCommas(amount) : amount;

    // Check if this is a deallocation (amount is zero)
    const isDeallocation = cashAmount === 0;

    // For allocation (not deallocation), validate amount is positive
    if (
      !isDeallocation &&
      (!cashAmount || cashAmount <= 0 || isNaN(cashAmount))
    ) {
      notification.error({
        message: "Validation Error",
        description: "Please enter a valid cash amount greater than 0",
      });
      return;
    }

    // Find the invoice to validate against remaining amount
    const invoiceToAllocate = invoices.find((inv) => inv.id === invoiceId);
    if (!invoiceToAllocate) {
      notification.error({
        message: "Validation Error",
        description: "Invoice not found",
      });
      return;
    }

    // Prevent allocation to cancelled invoices
    if (!isDeallocation && invoiceToAllocate.status === "cancelled") {
      notification.error({
        message: "Validation Error",
        description: `Cannot allocate cash to cancelled invoice #${
          invoiceToAllocate.number || invoiceNumber
        }. Cancelled invoices cannot receive allocations.`,
      });
      return;
    }

    // Only validate remaining amount for new allocations (not deallocations)
    if (!isDeallocation) {
      const remainingAmount = parseFloat(invoiceToAllocate.remainingAmount);
      const epsilon = 0.001; // Small tolerance for floating point comparison

      // If updating an existing allocation, add its current amount to the remaining amount
      let adjustedRemainingAmount = remainingAmount;
      if (allocationId && invoiceToAllocate.current_allocation_id) {
        adjustedRemainingAmount += parseFloat(
          invoiceToAllocate.current_allocation_amount || 0
        );
      }

      if (cashAmount > adjustedRemainingAmount + epsilon) {
        notification.error({
          message: "Validation Error",
          description: `Cash amount (${cashAmount.toFixed(
            2
          )}) exceeds the remaining balance (${adjustedRemainingAmount.toFixed(
            2
          )}) for invoice #${invoiceToAllocate.number}`,
        });
        return;
      }
    }

    // Validate cash has enough unallocated amount
    if (cash && !isDeallocation) {
      const cashAmount = parseFloat(cash.total_amount || 0);
      const allocatedAmount = parseFloat(cash.allocated_amount || 0);
      const remainingCash = cashAmount - allocatedAmount;

      // If updating an existing allocation, add its current amount to the remaining amount
      let adjustedRemainingCash = remainingCash;
      if (allocationId && invoiceToAllocate.current_allocation_id) {
        adjustedRemainingCash += parseFloat(
          invoiceToAllocate.current_allocation_amount || 0
        );
      }

      // Add a small epsilon to handle floating point precision issues
      const epsilon = 0.001;

      if (amount > adjustedRemainingCash + epsilon) {
        notification.error({
          message: "Validation Error",
          description: `Cash amount (${amount}) exceeds the remaining cash amount (${adjustedRemainingCash.toFixed(
            2
          )})`,
        });
        return;
      }
    }

    try {
      setLoadingAllocation(true);

      // Prepare allocation data
      const allocationData = {
        invoice_id: invoiceId,
        amount: cashAmount,
        partner_id: selectedPartner,
      };

      // If this is updating an existing allocation, include the allocation_id
      if (allocationId) {
        allocationData.allocation_id = allocationId;
      }

      const result = await allocateCash(cashId, allocationData);

      // Show success notification with the correct invoice number
      if (isDeallocation) {
        // For unallocation, immediately update the UI state
        // This ensures the button changes from Unallocate to Allocate right away
        setInvoices((prevInvoices) => {
          return prevInvoices.map((inv) => {
            if (inv.id === invoiceId) {
              return {
                ...inv,
                current_allocation_id: null,
                current_allocation_amount: 0,
                cashAmount: 0,
              };
            }
            return inv;
          });
        });

        // Update allocations array to remove this allocation
        setAllocations((prevAllocations) =>
          prevAllocations.filter(
            (alloc) =>
              !(alloc.invoice === invoiceId || alloc.invoice_id === invoiceId)
          )
        );

        notification.success({
          message: "Cash Unallocated",
          description: `Successfully unallocated cash from invoice #${invoiceNumber}`,
        });
      } else {
        notification.success({
          message: "Cash Allocated",
          description: `Successfully ${
            allocationId ? "updated" : "allocated"
          } ${amount} to invoice #${invoiceNumber}`,
        });
      }

      // Clear cache to ensure fresh data on next page load
      clearAccountingCache();

      // Always refetch cash data to ensure we have the latest information
      fetchCash();

      // Refresh the invoices list
      if (selectedPartner) {
        fetchInvoices(selectedPartner, showPaidInvoices);
      }

      // Notify parent component with cashId to update just that row
      if (onSuccess) {
        onSuccess({ cashId, type: "cash" });
      }
    } catch (error) {
      console.error("Error allocating cash:", error);

      // Provide more specific error messages
      let errorMessage = "Failed to allocate cash";

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 400) {
        errorMessage =
          "Invalid cash data. The amount may exceed the available cash amount or invoice remaining balance.";
      } else if (error.response?.status === 404) {
        errorMessage = "Invoice or cash not found.";
      } else if (error.response?.status === 403) {
        errorMessage = "You do not have permission to allocate this cash.";
      }

      notification.error({
        message: "Allocation Error",
        description: errorMessage,
      });
    } finally {
      setLoadingAllocation(false);
    }
  };

  const handleCashChange = (invoiceId, e) => {
    // Get the raw input value
    const inputValue = e.target.value;

    // Update the cash amount for the specific invoice
    const updatedInvoices = invoices.map((invoice) => {
      if (invoice.id === invoiceId) {
        // Store the raw input value to prevent cursor jumping
        // Only convert to number when actually submitting
        return {
          ...invoice,
          cashAmount: inputValue,
        };
      }
      return invoice;
    });

    setInvoices(updatedInvoices);

    // Auto-check the invoice if a valid cash amount is entered
    const numericValue = parseFloat(inputValue);
    if (!isNaN(numericValue) && numericValue > 0) {
      // Add to selected invoices if not already selected
      if (!selectedInvoiceIds.includes(invoiceId)) {
        setSelectedInvoiceIds((prev) => [...prev, invoiceId]);
      }
    } else {
      // Remove from selected invoices if cash amount is cleared or invalid
      setSelectedInvoiceIds((prev) => prev.filter((id) => id !== invoiceId));
    }
  };

  // Handle row selection change (for multiple selection)
  const handleRowSelectionChange = (selectedRowKeys) => {
    setSelectedInvoiceIds(selectedRowKeys);
  };

  // Handle allocate all checked invoices
  const handleAllocateAllChecked = async () => {
    if (selectedInvoiceIds.length === 0) {
      notification.warning({
        message: "No Invoices Selected",
        description: "Please select at least one invoice to allocate cash.",
      });
      return;
    }

    setLoadingAllocation(true);

    try {
      // Process each selected invoice one by one
      for (const invoiceId of selectedInvoiceIds) {
        const invoice = invoices.find((inv) => inv.id === invoiceId);

        if (!invoice) continue;

        // Skip invoices that are already fully paid, cancelled, or have no remaining balance
        if (
          invoice.status === "paid" ||
          invoice.status === "cancelled" ||
          parseFloat(invoice.remainingAmount) <= 0
        ) {
          continue;
        }

        // Get the allocation ID if it exists
        const allocationId = invoice.current_allocation_id;

        // Use the entered cash amount if available, otherwise use the remaining amount
        // IMPORTANT: Use parseNumberWithCommas to handle comma-formatted numbers (e.g., "2,007.430")
        let amount;
        const parsedCashAmount = parseNumberWithCommas(invoice.cashAmount);
        if (parsedCashAmount > 0) {
          amount = parsedCashAmount;
        } else {
          amount = parseFloat(invoice.remainingAmount);
        }

        // Allocate cash to this invoice
        await handleAllocateCash(invoice.id, amount, allocationId);
      }
    } catch (error) {
      console.error("Error in bulk allocation:", error);
      notification.error({
        message: "Bulk Allocation Error",
        description:
          "An error occurred while allocating cash to multiple invoices.",
      });
    } finally {
      setLoadingAllocation(false);
    }
  };

  // Update allocation status from cash data
  const updateAllocationStatus = (cash) => {
    if (!cash) {
      setAllocationStatus(null);
      return;
    }

    try {
      // Set allocations from cash data
      if (cash.cash_allocations) {
        setAllocations(cash.cash_allocations);
      }

      // Use allocation status directly from backend if available
      if (cash.allocation_status) {
        setAllocationStatus({
          ...cash.allocation_status,
          totalAllocated: parseFloat(cash.allocated_amount || 0),
          percentAllocated: cash.percent_allocated || 0,
        });
        return;
      }

      // Fallback calculation if backend doesn't provide allocation status
      const cashAmount = Math.abs(parseFloat(cash.total_amount || 0));
      const totalAllocated = parseFloat(cash.allocated_amount || 0);
      const percentAllocated =
        cashAmount > 0 ? (totalAllocated / cashAmount) * 100 : 0;

      // Determine status based on remaining amount (more accurate than percentage)
      const remainingAmount = cashAmount - totalAllocated;
      let status = null;
      // Only mark as fully allocated if remaining is essentially 0
      if (remainingAmount <= 0.001) {
        status = {
          type: "fully_allocated",
          label: "Fully Allocated",
          color: "success",
        };
      } else if (totalAllocated > 0) {
        status = {
          type: "partially_allocated",
          label: "Partially Allocated",
          color: "warning",
        };
      } else {
        status = {
          type: "unallocated",
          label: "Unallocated",
          color: "default",
        };
      }

      setAllocationStatus({
        ...status,
        totalAllocated,
        percentAllocated,
      });
    } catch (error) {
      console.error("Error updating allocation status:", error);
      setAllocationStatus({
        type: "error",
        label: "Error",
        color: "error",
        totalAllocated: 0,
        percentAllocated: 0,
      });
    }
  };

  // Handle show paid invoices toggle
  const handleShowPaidInvoicesChange = (checked) => {
    setShowPaidInvoices(checked);
  };

  // Sort invoices by oldest to newest (issue_date ascending) for allocation
  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      const dateA = a.issue_date ? new Date(a.issue_date) : new Date(0);
      const dateB = b.issue_date ? new Date(b.issue_date) : new Date(0);
      return dateA - dateB; // Oldest first
    });
  }, [invoices]);

  // Get cash columns from the imported function
  const cashColumns = getCashColumns(allocationStatus);

  // Get invoice columns from the imported function
  const invoiceColumns = getInvoiceColumns({
    handleCashChange,
    handleAllocateCash,
    loadingAllocation,
    parseNumberFn: parseNumberWithCommas,
    onViewPartner,
  });

  // Handle back button
  const handleBack = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/accounting/cash");
    }
  };

  if (!cashId || (!selectedBrand && !primaryBrand)) {
    return (
      <Card title="Cash Allocation">
        <Text type="secondary">No cash selected or brand not selected</Text>
        <div style={{ marginTop: 16 }}>
          <Button
            type="primary"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
          >
            Back to Cash
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card size="small" className="cash-allocation-card">
      {loadingCash ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <>
          <Title level={5}>Cash Details</Title>
          {cash && (
            <Table
              dataSource={[cash]}
              columns={cashColumns}
              pagination={false}
              rowKey="id"
              size="small"
            />
          )}

          <Divider />

          <Row gutter={16} justify="space-between" align="middle">
            <Col span={8}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>Select Partner:</Text>
                <Select
                  placeholder="Select a partner"
                  style={{ width: "100%", marginTop: 8 }}
                  onChange={handlePartnerChange}
                  value={selectedPartner}
                  showSearch
                  optionFilterProp="children"
                >
                  {Array.isArray(partners) && partners.length > 0 ? (
                    partners.map((partner) => (
                      <Option key={partner.id} value={partner.id}>
                        {partner.name} ({partner.partner_type})
                      </Option>
                    ))
                  ) : (
                    <Option value="" disabled>
                      No partners available
                    </Option>
                  )}
                </Select>
              </div>
            </Col>
            <Col>
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Space>
                  <span>Show Paid</span>
                  <Switch
                    checked={showPaidInvoices}
                    onChange={handleShowPaidInvoicesChange}
                  />
                </Space>
              </div>
            </Col>
          </Row>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 16,
              marginBottom: 16,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              Partner Invoices
            </Title>
            <Button
              type="primary"
              onClick={handleAllocateAllChecked}
              disabled={loadingAllocation || selectedInvoiceIds.length === 0}
              loading={loadingAllocation}
            >
              Allocate All Checked
            </Button>
          </div>
          <div className="fixed-table-container">
            <Table
              dataSource={sortedInvoices}
              columns={invoiceColumns}
              pagination={{
                ...pagination,
                total: sortedInvoices.length,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ["50", "100", "200"],
                showTotal: (total) => `Total ${total} invoices`,
                onChange: (page, pageSize) => {
                  setPagination({ current: page, pageSize });
                },
                onShowSizeChange: (current, size) => {
                  setPagination({ current: 1, pageSize: size });
                },
              }}
              rowKey="id"
              size="small"
              loading={loadingInvoices}
              scroll={{ y: 400 }}
              tableLayout="auto"
              sticky={{ offsetHeader: 0 }}
              rowClassName={(record) =>
                record.is_allocated ? "table-success-outline-invoice" : ""
              }
              rowSelection={{
                selectedRowKeys: selectedInvoiceIds,
                onChange: handleRowSelectionChange,
                getCheckboxProps: (record) => ({
                  disabled:
                    parseFloat(record.remainingAmount) <= 0 ||
                    record.status === "paid" ||
                    record.status === "cancelled",
                }),
              }}
            />
          </div>
        </>
      )}
    </Card>
  );
};

export default CashAllocation;
